/**
 * CRM Context
 * Global state management for CRM module
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import crmApi from '../services/crmApi';
import adminAPI from '../services/adminApi';
import { toast } from 'react-hot-toast';

const CRMContext = createContext({
  customers: [], leads: [], opportunities: [], trainings: [], workshops: [],
  categories: [], products: [], subProducts: [], loading: true, error: null,
  fetchCustomers: async () => {}, createCustomer: async () => {},
  updateCustomer: async () => {}, deleteCustomer: async () => {},
  fetchLeads: async () => {}, createLead: async () => {},
  convertLead: async () => {}, archiveLead: async () => {}, deleteLead: async () => {},
});

export const useCRM = () => useContext(CRMContext);

export const CRMProvider = ({ children }) => {
  // State management
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [subProducts, setSubProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Users cache (shared across all CRM components)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLastFetch, setUsersLastFetch] = useState(null);
  const [usersLastRolesKey, setUsersLastRolesKey] = useState(null);
  const USERS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // ============================================================================
  // CUSTOMER METHODS
  // ============================================================================

  /**
   * Fetch users for assignment dropdowns
   * - Uses /users/assignable endpoint (no USERS:PROFILES:READ required)
   * - Returns up to 1000 active users with minimal fields
   */
  const fetchUsers = useCallback(async (forceRefresh = false) => {
    // Read global role filter from localStorage (configured in Lead Pools → Roles button)
    const globalRoles = (() => { try { return JSON.parse(localStorage.getItem('crmLeadPoolAllowedRoles') || '[]'); } catch { return []; } })();
    const rolesKey = [...globalRoles].sort().join(',');

    // Check cache validity — also bust if roles config has changed since last fetch
    if (!forceRefresh && usersLastFetch && Date.now() - usersLastFetch < USERS_CACHE_DURATION && users.length > 0 && usersLastRolesKey === rolesKey) {
      console.log('[CRMContext] Using cached users list');
      return users;
    }

    try {
      setUsersLoading(true);

      const baseParams = globalRoles.length > 0 ? { roles: globalRoles.join(',') } : {};
      const limit = 100;
      let page = 1;
      let totalPages = 1;
      const allUsers = [];

      while (page <= totalPages) {
        const response = await adminAPI.getAssignableUsers({ ...baseParams, page, limit });
        const responseData = response.data?.data || {};
        const usersList = Array.isArray(responseData.users) ? responseData.users : Array.isArray(responseData) ? responseData : [];
        const pagination = responseData.pagination || {};
        allUsers.push(...usersList);
        totalPages = pagination.pages || pagination.totalPages || 1;
        page += 1;
      }

      const usersData = Array.from(new Map(allUsers.map(u => [String(u._id), u])).values());

      setUsers(usersData);
      setUsersLastFetch(Date.now());
      setUsersLastRolesKey(rolesKey);

      console.log(`[CRMContext] Loaded ${usersData.length} assignable users (roles: ${rolesKey || 'all'})`);

      return usersData;
    } catch (err) {
      console.error('[CRMContext] Failed to fetch users:', err);
      toast.error('Failed to load users');
      return [];
    } finally {
      setUsersLoading(false);
    }
  }, [usersLastFetch, usersLastRolesKey, users, USERS_CACHE_DURATION]);

  /**
   * Search users by name or email (bypasses cache, always queries backend)
   */
  const searchUsers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      return users.slice(0, 100); // Return cached users if no search
    }

    try {
      const response = await adminAPI.getUsers({ 
        search: searchTerm, 
        page: 1, 
        limit: 50 
      });
      const searchResults = response.data.data.users || response.data.data || [];
      return Array.isArray(searchResults) ? searchResults : [];
    } catch (err) {
      console.error('[CRMContext] User search failed:', err);
      return [];
    }
  }, [users]);

  /**
   * Fetch all customers
   */
  const fetchCustomers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getCustomers(params);
      // API returns { success, data: [...], pagination }
      setCustomers(response.data.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch customers';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new customer
   */
  const createCustomer = useCallback(async (customerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createCustomer(customerData);
      setCustomers(prev => [response.data.data, ...prev]);
      toast.success('Customer created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create customer';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update customer
   */
  const updateCustomer = useCallback(async (id, customerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateCustomer(id, customerData);
      setCustomers(prev => 
        prev.map(customer => 
          customer._id === id ? response.data.data : customer
        )
      );
      toast.success('Customer updated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update customer';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete customer
   */
  const deleteCustomer = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteCustomer(id);
      setCustomers(prev => prev.filter(customer => customer._id !== id));
      toast.success('Customer deleted successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete customer';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // LEAD METHODS
  // ============================================================================

  /**
   * Fetch all leads
   */
  const fetchLeads = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getLeads(params);
      setLeads(response.data.data.leads);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch leads';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new lead
   */
  const createLead = useCallback(async (leadData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createLead(leadData);
      setLeads(prev => [response.data.data, ...prev]);
      toast.success('Lead created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create lead';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Convert lead to customer
   */
  const convertLead = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.convertLead(id);
      setLeads(prev => prev.filter(lead => lead._id !== id));
      toast.success('Lead converted to customer successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to convert lead';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive lead
   */
  const archiveLead = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.archiveLead(id);
      setLeads(prev => prev.filter(lead => lead._id !== id));
      toast.success('Lead archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive lead';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete lead (same as archive since backend actually deletes)
   */
  const deleteLead = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.archiveLead(id);
      setLeads(prev => prev.filter(lead => lead._id !== id));
      toast.success('Lead deleted successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete lead';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // OPPORTUNITY METHODS
  // ============================================================================

  /**
   * Fetch all opportunities
   */
  const fetchOpportunities = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getOpportunities(params);
      setOpportunities(response.data.data.opportunities);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch opportunities';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new opportunity
   */
  const createOpportunity = useCallback(async (opportunityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createOpportunity(opportunityData);
      setOpportunities(prev => [response.data.data, ...prev]);
      toast.success('Opportunity created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create opportunity';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Move opportunity to next stage
   */
  const moveOpportunityStage = useCallback(async (id, stage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.moveOpportunityStage(id, stage);
      setOpportunities(prev => 
        prev.map(opp => 
          opp._id === id ? response.data.data : opp
        )
      );
      toast.success('Opportunity stage updated');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update opportunity';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // TRAINING METHODS
  // ============================================================================

  /**
   * Fetch all trainings
   */
  const fetchTrainings = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getTrainings(params);
      setTrainings(response.data.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch trainings';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new training
   */
  const createTraining = useCallback(async (trainingData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createTraining(trainingData);
      setTrainings(prev => [response.data.data, ...prev]);
      toast.success('Training created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create training';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update training
   */
  const updateTraining = useCallback(async (id, trainingData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateTraining(id, trainingData);
      setTrainings(prev => 
        prev.map(training => 
          training._id === id ? response.data.data : training
        )
      );
      toast.success('Training updated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update training';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive training
   */
  const archiveTraining = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteTraining(id);
      setTrainings(prev => prev.filter(training => training._id !== id));
      toast.success('Training archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive training';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // WORKSHOP METHODS
  // ============================================================================

  /**
   * Fetch all workshops
   */
  const fetchWorkshops = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getWorkshops(params);
      setWorkshops(response.data.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch workshops';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new workshop
   */
  const createWorkshop = useCallback(async (workshopData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createWorkshop(workshopData);
      setWorkshops(prev => [response.data.data, ...prev]);
      toast.success('Workshop created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create workshop';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update workshop
   */
  const updateWorkshop = useCallback(async (id, workshopData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateWorkshop(id, workshopData);
      setWorkshops(prev => 
        prev.map(workshop => 
          workshop._id === id ? response.data.data : workshop
        )
      );
      toast.success('Workshop updated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update workshop';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive workshop
   */
  const archiveWorkshop = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteWorkshop(id);
      setWorkshops(prev => prev.filter(workshop => workshop._id !== id));
      toast.success('Workshop archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive workshop';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete workshop (same as archive since backend actually deletes)
   */
  const deleteWorkshop = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteWorkshop(id);
      setWorkshops(prev => prev.filter(workshop => workshop._id !== id));
      toast.success('Workshop deleted successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete workshop';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // EDUCATIONAL PRODUCT MANAGEMENT METHODS
  // ============================================================================

  /**
   * Fetch all categories
   */
  const fetchCategories = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getCategories(params);
      setCategories(response.data.categories || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch categories';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new category
   */
  const createCategory = useCallback(async (categoryData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createCategory(categoryData);
      setCategories(prev => [response.data.data.category, ...prev]);
      // Toast message will be shown by the component or backend response
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create category';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update category
   */
  const updateCategory = useCallback(async (id, categoryData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateCategory(id, categoryData);
      setCategories(prev => 
        prev.map(category => 
          category._id === id ? response.data.data : category
        )
      );
      // Toast message will be shown by the component or backend response
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update category';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive category
   */
  const archiveCategory = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteCategory(id);
      setCategories(prev => prev.filter(category => category._id !== id));
      toast.success('Category archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive category';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all products
   */
  const fetchProducts = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getProducts(params);
      setProducts(response.data.products || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch products';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new product
   */
  const createProduct = useCallback(async (productData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createProduct(productData);
      setProducts(prev => [response.data.data, ...prev]);
      toast.success('Product created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update product
   */
  const updateProduct = useCallback(async (id, productData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateProduct(id, productData);
      setProducts(prev => 
        prev.map(product => 
          product._id === id ? response.data.data : product
        )
      );
      toast.success('Product updated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive product
   */
  const archiveProduct = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteProduct(id);
      setProducts(prev => prev.filter(product => product._id !== id));
      toast.success('Product archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all sub-products
   */
  const fetchSubProducts = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.getSubProducts(params);
      setSubProducts(response.data.subProducts || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch sub-products';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create new sub-product
   */
  const createSubProduct = useCallback(async (subProductData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.createSubProduct(subProductData);
      setSubProducts(prev => [response.data.data, ...prev]);
      toast.success('Sub-product created successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create sub-product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update sub-product
   */
  const updateSubProduct = useCallback(async (id, subProductData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await crmApi.updateSubProduct(id, subProductData);
      setSubProducts(prev => 
        prev.map(subProduct => 
          subProduct._id === id ? response.data.data : subProduct
        )
      );
      toast.success('Sub-product updated successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update sub-product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Archive sub-product
   */
  const archiveSubProduct = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await crmApi.deleteSubProduct(id);
      setSubProducts(prev => prev.filter(subProduct => subProduct._id !== id));
      toast.success('Sub-product archived successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to archive sub-product';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Context value
  const value = {
    // State
    customers,
    leads,
    opportunities,
    trainings,
    workshops,
    categories,
    products,
    subProducts,
    loading,
    error,
    
    // Customer methods
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    
    // Lead methods
    fetchLeads,
    createLead,
    convertLead,
    archiveLead,
    deleteLead,
    
    // Opportunity methods
    fetchOpportunities,
    createOpportunity,
    moveOpportunityStage,
    
    // Training methods
    fetchTrainings,
    createTraining,
    updateTraining,
    archiveTraining,
    
    // Workshop methods
    fetchWorkshops,
    createWorkshop,
    updateWorkshop,
    archiveWorkshop,
    deleteWorkshop,
    
    // Educational Product methods
    fetchCategories,
    createCategory,
    updateCategory,
    archiveCategory,
    fetchProducts,
    createProduct,
    updateProduct,
    archiveProduct,
    fetchSubProducts,
    createSubProduct,
    updateSubProduct,
    archiveSubProduct,
    
    // Direct API access (for components that need more control)
    api: crmApi,

    // Users (cached)
    users,
    usersLoading,
    fetchUsers,
    searchUsers,
    refreshUsers: () => fetchUsers(true),
  };

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
};

export default CRMContext;
